import React, { useContext, useState, useRef, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';

import { usePaymentOptions } from 'common/hooks/usePaymentOptions';
import {
    PaymentMethod,
    PaymentType,
    PaymentTypeFee,
    PaymentSubType,
} from 'common/api/e-comm/payment-types';
import { monetizeWithDecimal } from 'common/utils/strings';
import { AchAccount } from 'common/api/e-comm/ach/models/AchAccount';
import { SavedCreditCardWithCloMetaData } from 'common/api/e-comm/models/CreditCard';
import { PartialRecord } from 'common/definitions/PartialRecord';
import { PaymentInfo, getDefaultPaymentId } from 'features/order-summary/utils/useCartPayment';
import { RadioItem } from 'features/product/components/RadioItem';
import { Checkbox } from 'components/checkbox/Checkbox';
import { Link } from 'react-router-dom';
import { Routes } from 'routes';
import {
    selectDefaultPaymentType,
    selectSavedCreditCardsWithChildIxoPayCards,
} from 'common/features/store/duck/account/duck';
import { useSelector } from 'react-redux';
import { useRootSelector } from 'common/features/featuresReducer';
import { colors } from 'styles/settings';
import { isInIframe, mainWebsiteOrigin } from 'utils';
import { selectIsCardLinkingThroughCheckoutWebEnabled } from 'common/features/store/duck/ffs';
import { CardLinkingStatusAndButton, CardLinkingTermsModal } from './LocalDiningLinking';
import { OrderScreenContext } from 'features/order-summary/screens/CheckoutScreen';
import { useModalControls } from 'hooks/useModalControls';
import { selectIsCAOrganization } from 'common/features/store/duck/home/duck';
import { IS_FLIPGIVE } from 'common/env';

type OrderPaymentOptionInfo = PaymentInfo & {
    title: React.ReactNode;
    description: string;
    disabled?: boolean;
    link?: string;
    linkText?: string;
    disabledRadioOnly?: boolean;
    sortId: number;
    action?: () => void;
    card?: SavedCreditCardWithCloMetaData;
};

interface OrderPaymentOptionsProps {
    shipToHome: boolean;
    selectedPayment?: PaymentInfo;
    onPaymentChange: (payment: PaymentInfo) => unknown;
    makeThisTheDefaultPayment: boolean;
    setMakeThisTheDefaultPayment: (state: boolean) => void;
}

const formatFee = (fee: PaymentTypeFee | null) => {
    if (!fee) {
        return '$0';
    }
    return fee.isPercentage ? `${fee.amount.toFixed(1)}%` : monetizeWithDecimal(fee.amount);
};

const hasPaymentType = (paymentType: PaymentType, paymentTypes: PaymentType[] | null) => {
    if (!paymentTypes) {
        return false;
    }
    return Boolean(paymentTypes.find((pt) => pt === paymentType));
};

const getFee = (
    paymentType: PaymentSubType,
    paymentTypeFees: PartialRecord<PaymentSubType, PaymentTypeFee> | null
) => {
    return (paymentTypeFees && paymentTypeFees[paymentType]) || null;
};

const getPaymentOptions = (
    paymentTypes: PaymentType[] | null,
    paymentTypeFees: PartialRecord<PaymentType, PaymentTypeFee> | null,
    achAccount: AchAccount | null,
    savedCreditCards: SavedCreditCardWithCloMetaData[] | null,
    shipToHome: boolean,
    isCheckEnabled: boolean,
    isCAOrganization: boolean,
    defaultPaymentType?: PaymentMethod | null
) => {
    const paymentOptions: OrderPaymentOptionInfo[] = [];

    const achFee = getFee(PaymentSubType.BankAccount, paymentTypeFees);
    const creditCardFee = getFee(PaymentSubType.Credit, paymentTypeFees);
    const debitCardFee = getFee(PaymentSubType.Debit, paymentTypeFees);

    const checkPayment: OrderPaymentOptionInfo = {
        id: PaymentType.Check,
        paymentType: PaymentType.Check,
        paymentSubType: null,
        title: 'Pay Coordinator Directly',
        description: isCheckEnabled
            ? 'Pay with a check or cash. Not eligible for ship to home.'
            : 'This payment option has been disabled by your coordinator.',
        disabled: !isCheckEnabled,
        sortId: 3,
    };

    const creditCards = savedCreditCards?.filter(
        (savedCreditCards) => savedCreditCards.cardSubType === PaymentSubType.Credit
    );
    const debitCards = savedCreditCards?.filter(
        (savedCreditCards) => savedCreditCards.cardSubType === PaymentSubType.Debit
    );

    if (
        hasPaymentType(PaymentType.BankAccount, paymentTypes) &&
        achAccount &&
        achAccount.availableForPayment
    ) {
        const achPayment: OrderPaymentOptionInfo = {
            id: achAccount.id,
            paymentType: PaymentType.BankAccount,
            paymentSubType: null,
            title: (
                <span>
                    Bank Account{' '}
                    <span data-recording-sensitive className="font-normal text-grey-darkest">
                        ({achAccount.accountNumber})
                    </span>
                </span>
            ),
            description: `${formatFee(achFee)} Convenience Fee`,
            sortId: 0,
        };

        paymentOptions.push(achPayment);
    }

    if (hasPaymentType(PaymentType.CreditCard, paymentTypes)) {
        debitCards?.forEach((debitCard) => {
            paymentOptions.push({
                id: debitCard.id.toString(),
                paymentType: PaymentType.CreditCard,
                paymentSubType: PaymentSubType.Debit,
                title: (
                    <span>
                        {IS_FLIPGIVE ? 'Card' : 'Debit Card'}
                        <span data-recording-sensitive className="font-normal text-grey-darkest">
                            {' '}
                            ({debitCard.maskedCardNumber})
                        </span>
                    </span>
                ),
                description: isCAOrganization ? '' : `${formatFee(debitCardFee)} Convenience Fee`,
                sortId: 1,
                card: debitCard,
            });
        });

        if (!isCAOrganization) {
            paymentOptions.push({
                id: 'new-debit-card',
                paymentType: PaymentType.CreditCard,
                paymentSubType: PaymentSubType.Debit,
                title: IS_FLIPGIVE ? 'Add Card' : 'Add Debit Card',
                description: isCAOrganization
                    ? ''
                    : `${formatFee(debitCardFee)} convenience fee applies to each transaction`,
                sortId: 5,
            });
        }

        creditCards?.forEach((creditCard) => {
            paymentOptions.push({
                id: creditCard.id.toString(),
                paymentType: PaymentType.CreditCard,
                paymentSubType: PaymentSubType.Credit,
                title: (
                    <span>
                        {IS_FLIPGIVE ? 'Card' : 'Pay with credit card'}
                        <span data-recording-sensitive className="font-normal text-grey-darkest">
                            {' '}
                            ({creditCard.maskedCardNumber})
                        </span>
                    </span>
                ),
                description: isCAOrganization ? '' : `${formatFee(creditCardFee)} Convenience Fee`,
                sortId: 2,
                card: creditCard,
            });
        });

        paymentOptions.push({
            id: 'new-credit-card',
            paymentType: PaymentType.CreditCard,
            paymentSubType: PaymentSubType.Credit,
            title: IS_FLIPGIVE
                ? 'Add Card'
                : isCAOrganization
                ? 'Add Debit or Credit Card'
                : 'Add Credit Card',
            description: isCAOrganization
                ? ''
                : `${formatFee(creditCardFee)} convenience fee applies to each transaction`,
            sortId: 6,
        });
    }

    // NOTE: add check-payment as the top item
    if (!shipToHome && !isCAOrganization) {
        paymentOptions.push(checkPayment);
    }

    if (
        hasPaymentType(PaymentType.BankAccount, paymentTypes) &&
        paymentOptions.filter((option) => option.paymentType === PaymentType.BankAccount).length ===
            0
    ) {
        paymentOptions.push({
            id: 'BankAccount',
            paymentType: PaymentType.BankAccount,
            paymentSubType: null,
            title: 'Add a Bank Account',
            description: `${formatFee(
                getFee(PaymentSubType.BankAccount, paymentTypeFees)
            )} convenience fee applies to each transaction`,
            link: Routes.ShopAccountSettingsPaymentOptionsTab,
            linkText: 'Add a Bank Account in Personal Settings',
            disabled: true,
            disabledRadioOnly: true,
            sortId: 4,
            action: isInIframe()
                ? () => {
                      window.parent.postMessage(
                          { type: 'navigateTo', url: Routes.ShopAccountSettingsPaymentOptionsTab },
                          mainWebsiteOrigin()
                      );
                  }
                : undefined,
        });
    }

    const defaultId =
        defaultPaymentType?.creditCardId || defaultPaymentType?.achAccountId || 'Check';

    return paymentOptions.sort((a, b) =>
        a.id === `${defaultId}` ? -1 - b.sortId : a.sortId - b.sortId
    );
};

const getSelectedPaymentOption = (
    defaultPaymentType: PaymentMethod | null,
    selectedPayment: PaymentInfo | undefined,
    paymentOptions: OrderPaymentOptionInfo[],
    isCheckEnabled: boolean
) => {
    if (selectedPayment && (selectedPayment?.paymentType !== PaymentType.Check || isCheckEnabled)) {
        return selectedPayment.id;
    }

    switch (defaultPaymentType?.paymentType) {
        case 'Check':
            // NOTE: When check payment is not enabled then
            return isCheckEnabled ? paymentOptions.find((option) => option.id === 'Check')?.id : '';
        case 'BankAccount':
            return paymentOptions.find((option) => option.id === defaultPaymentType.achAccountId)
                ?.id;
        case 'CreditCard':
            return paymentOptions.find(
                (option) => parseInt(option.id as string) === defaultPaymentType.creditCardId
            )?.id;
        default:
            return '';
    }
};

export const OrderPaymentOptionsWithDebit = ({
    shipToHome,
    selectedPayment,
    onPaymentChange,
    makeThisTheDefaultPayment,
    setMakeThisTheDefaultPayment,
    children,
}: React.PropsWithChildren<OrderPaymentOptionsProps>) => {
    const defaultPaymentType = useSelector(selectDefaultPaymentType);
    const { paymentTypes, paymentTypeFees, achAccount, isLoading } = usePaymentOptions();
    const savedCreditCards = useSelector(selectSavedCreditCardsWithChildIxoPayCards);
    const { setCartInfo, cartInfo } = useContext(OrderScreenContext);
    const isCheckEnabled = Boolean(paymentTypes?.includes(PaymentType.Check));
    const isCAOrganization = useSelector(selectIsCAOrganization);
    const paymentOptions = getPaymentOptions(
        paymentTypes,
        paymentTypeFees,
        achAccount,
        savedCreditCards,
        shipToHome,
        isCheckEnabled,
        isCAOrganization,
        defaultPaymentType
    );
    const isLocalDiningCheckoutEnabled = useSelector(selectIsCardLinkingThroughCheckoutWebEnabled);
    const [onChangeLoadingStatus, setOnChangeLoadingStatus] = useState<{ [id: string]: boolean }>(
        {}
    );
    const cloTermsAndConditionsModal = useModalControls();
    const cardIdToLinkToClo = useRef<number>(0);

    const internalOnPaymentChange = async (id: string) => {
        if (['new-credit-card', 'new-debit-card'].includes(id)) {
            setMakeThisTheDefaultPayment(true);
        } else {
            setMakeThisTheDefaultPayment(false);
        }

        const selectedPaymentOption = paymentOptions.find((opt) => opt.id.toString() === id);

        if (selectedPaymentOption) {
            const { paymentType, id } = selectedPaymentOption;
            const value = onPaymentChange({
                paymentType,
                id,
                paymentSubType: selectedPaymentOption.paymentSubType,
            });
            if (value instanceof Promise) {
                setOnChangeLoadingStatus({
                    ...onChangeLoadingStatus,
                    [id]: true,
                });

                try {
                    await value;
                } finally {
                    setOnChangeLoadingStatus({
                        ...onChangeLoadingStatus,
                        [id]: false,
                    });
                }
            }
        }
    };

    const selectedPaymentId = getSelectedPaymentOption(
        defaultPaymentType,
        selectedPayment,
        paymentOptions,
        isCheckEnabled
    );

    // passing no deps in to useMemo on purpose, we only want to call this once
    // on component initial load to set pass the default payment up to the parent component,
    // since the default payment doesn't trigger a radio onClick handler to set it initially.
    useEffect(() => {
        if (selectedPaymentId) {
            internalOnPaymentChange(selectedPaymentId.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (paymentOptions.length <= 0) {
        return <div>No payment methods available.</div>;
    }

    const defaultPaymentMethodId = getDefaultPaymentId(defaultPaymentType);

    const shouldHideDefaultCheckbox = (option: OrderPaymentOptionInfo) =>
        option.id === defaultPaymentMethodId.id ||
        option.id === defaultPaymentType?.achAccountId ||
        option.id === defaultPaymentType?.creditCardId ||
        option.id === 'new-credit-card' ||
        option.id === 'new-debit-card' ||
        option.id !== selectedPaymentId;

    // CASE: user unchecks the link w/ CLO checkbox for their
    // payment-method. We just set cardToLinkWithClo to 0 to
    // avoid the checkout submission from sending information
    // regarding CLO.
    const unEnrollFromRewardsNetwork = () => {
        cardIdToLinkToClo.current = 0;
        setCartInfo((cartInfo_) => ({
            ...cartInfo_,
            cardToLinkWithClo: 0,
            linkNewCardWithClo: false,
        }));
    };

    const linkWithCLO = () => {
        if (cardIdToLinkToClo.current) {
            setCartInfo({
                ...cartInfo,
                cardToLinkWithClo: cardIdToLinkToClo.current,
            });
        }
    };

    const onOpenTermsAndConditions = (cardId: number) => {
        cardIdToLinkToClo.current = cardId;
        cloTermsAndConditionsModal.toggleModalVisibility();
    };

    return (
        <div>
            <div>
                {!isLoading && (
                    <>
                        <AchStatusNotification />
                        <div>
                            {paymentOptions.map((option) => (
                                <div key={option.id} className="mt-4">
                                    {onChangeLoadingStatus[option.id] && <Skeleton height={35} />}
                                    {!onChangeLoadingStatus[option.id] && (
                                        <>
                                            <RadioItem
                                                disabled={option.disabled}
                                                onClick={() => {
                                                    // Hack: internalOnPaymentChange calls setCartInfo, same as unEnrollFromRewardsNetwork
                                                    // and it undoes the `clearing` effect that unEnrollFromRewardsNetwork attempts to do.
                                                    // nasty hack.. but we'll just live with it for now(timing issues :'()
                                                    setTimeout(() => {
                                                        unEnrollFromRewardsNetwork();
                                                    }, 300);
                                                    unEnrollFromRewardsNetwork();
                                                    return internalOnPaymentChange(
                                                        option.id.toString()
                                                    );
                                                }}
                                                selected={option.id === selectedPaymentId}
                                                text={option.title}
                                                value={option.id.toString()}
                                                key={option.id}
                                                disabledRadioOnly={option.disabledRadioOnly}
                                                fontTypeUnselected="font-semibold"
                                                fontColor={colors.brandDark}
                                            />
                                            <div className="text-grey-3 text-xs ml-7 mt-0.5">
                                                {option.description}
                                            </div>
                                            {option.link && option.linkText ? (
                                                option.action ? (
                                                    <a
                                                        onClick={option.action}
                                                        className="ml-7 text-xs underline"
                                                    >
                                                        {option.linkText}
                                                    </a>
                                                ) : (
                                                    <Link className="ml-7 text-xs" to={option.link}>
                                                        {option.linkText}
                                                    </Link>
                                                )
                                            ) : null}

                                            {shouldHideDefaultCheckbox(option) ? null : (
                                                <div className="mt-2 pl-7 text-sm text-grey-1">
                                                    <Checkbox
                                                        checked={makeThisTheDefaultPayment}
                                                        onChange={setMakeThisTheDefaultPayment}
                                                        id="chkDefaultPayment"
                                                        text="Save as default payment Method"
                                                        labelClassName="pl-1 text-xs"
                                                    />
                                                </div>
                                            )}

                                            {defaultPaymentMethodId.id === option.id ? (
                                                <div className="text-xs font-medium ml-7 mt-0.5 text-success">
                                                    Default Payment
                                                </div>
                                            ) : null}
                                            {selectedPaymentId === option.id &&
                                            isLocalDiningCheckoutEnabled &&
                                            option?.card &&
                                            option.card.id &&
                                            option.card.ixoPayCardId ? (
                                                <div className="ml-7 mt-0.5">
                                                    <CardLinkingStatusAndButton
                                                        isCheckboxDisabled={
                                                            option.card.enrolledInRewardsNetwork
                                                        }
                                                        unEnrollFromRewardsNetwork={
                                                            unEnrollFromRewardsNetwork
                                                        }
                                                        enrolledInRewardsNetwork={
                                                            option.card.id ===
                                                                cartInfo.cardToLinkWithClo ||
                                                            option.card.enrolledInRewardsNetwork
                                                        }
                                                        onClick={() =>
                                                            onOpenTermsAndConditions(
                                                                option.card.id!
                                                            )
                                                        }
                                                    />
                                                </div>
                                            ) : null}
                                            {option.id === selectedPaymentId ? children : null}
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
                {isLoading && <Skeleton />}
                <CardLinkingTermsModal
                    isVisible={cloTermsAndConditionsModal.isVisible}
                    onClose={cloTermsAndConditionsModal.toggleModalVisibility}
                    onLinkACard={linkWithCLO}
                />
            </div>
        </div>
    );
};

const AchStatusNotification = () => {
    const isAchLocked = Boolean(useRootSelector((s) => s.store.account.achAccount?.isLocked));

    if (isAchLocked) {
        return <AchIsLockedNotificationRenderer />;
    }

    return <></>;
};

const AchIsLockedNotificationRenderer = () => (
    <div className="mb-6">
        <p className="text-brand-dark font-semibold">
            Your online payment account has been locked.
        </p>
        <p className="text-sm">
            Please ask your coordinator to call the Customer Support team at 1-800-727-4715, option
            3, to unlock your account.
        </p>
    </div>
);
